import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  VlBreakdownRow,
  VlCandEstimate,
  VlComp,
  VlCompEstimate,
  VlFeedback,
  VlFreshness,
  VlGridCell,
  VlLot,
  VlLotDetail,
  VlMethodError,
  VlMethodKey,
  VlPlateNote,
  VlComment,
  VlCommentTopic,
  VlOutside,
  VlReasonCategory,
  VlMethodKpis,
  VlSegmentType,
  VlSort,
  VlSource,
  VlStatEstimate,
  VlStatus,
  VlSummary,
  VlValueResponse,
} from 'src/app/models/valuationLab';
import { SessionService } from 'src/app/services/session-service';
import { ValuationLabService, VlError } from 'src/app/services/valuation-lab-service';
import { localDevAccess } from 'src/app/dev-access';
import { RESEARCH_CATEGORIES } from './research/vlab-research.component';

export const COMMENT_TOPICS: { id: VlCommentTopic; label: string }[] = [
  { id: 'base_cohort', label: 'Base cohort' },
  { id: 'letters', label: 'Letters' },
  { id: 'own_sale', label: 'Own sale' },
  { id: 'stat_blend', label: 'Statistical blend' },
  { id: 'range_width', label: 'Range width' },
  { id: 'meaning', label: 'Meaning' },
  { id: 'data', label: 'Data' },
  { id: 'other', label: 'Other' },
];

export const WRONG_REASONS: { id: VlReasonCategory; label: string }[] = [
  { id: 'meaning_premium', label: 'Has its own meaning (e.g. a word/phrase)' },
  { id: 'different_class', label: 'Different class' },
  { id: 'not_comparable_format', label: 'Not the same format' },
  { id: 'outlier_price', label: 'Price looks wrong / outlier' },
  { id: 'bad_data', label: 'Bad data' },
  { id: 'too_old', label: 'Too old' },
  { id: 'other', label: 'Other' },
];

type Method = VlMethodKey | 'both';

interface MethodDef {
  key: VlMethodKey;
  /** short tag used in compact side-by-side cells: v1 / cand / stat */
  short: string;
  label: string;
}

/** Display order everywhere: Comparables v1 · Candidate · Statistical. */
const METHODS: MethodDef[] = [
  { key: 'comp', short: 'v1', label: 'Comparables v1' },
  { key: 'cand', short: 'cand', label: 'Candidate' },
  { key: 'stat', short: 'stat', label: 'Statistical' },
];

/** Low / central / high in pence, whichever method produced it. */
interface Est {
  low: number | null;
  mid: number | null;
  high: number | null;
}
type Breakdown = 'grid' | VlSegmentType;

/** What the Lot detail panel is showing: a scored lot, or an ad-hoc /value run. */
interface Detail {
  mode: 'lot' | 'value';
  lotKey: string | null;
  plate: string;
  houseLine: string;
  segment: string | null;
  source: string | null;
  status: VlStatus | null;
  comp: VlCompEstimate | null;
  compError: string | null;
  stat: VlStatEstimate | null;
  statError: string | null;
  cand: VlCandEstimate | null;
  candError: string | null;
  allIn: number | null;
  allInBasis: string | null;
  compErrPct: number | null;
  statErrPct: number | null;
  candErrPct: number | null;
  compInRange: boolean | null;
  statInRange: boolean | null;
  candInRange: boolean | null;
  explanation: string[];
  comps: VlComp[];
  candExplanation: string[];
  candComps: VlComp[];
  candCompGroups: { title: string; rows: VlComp[] }[];
  compsTotal: number | null;
  excluded: { key: string; n: number; label: string }[];
  feedback: VlFeedback[];
  /** owner meaning notes for this plate, latest first */
  plateNotes: VlPlateNote[];
  /** owner comments on how this lot was valued, with Claude's replies */
  comments: VlComment[];
  /** result above / below the range */
  outside: VlOutside | null;
}

/** Active stream filter that came from a grid cell or breakdown row. */
interface SegmentFilter {
  label: string;
  style?: string;
  length?: number;
  segment_type?: VlSegmentType;
  segment_key?: string;
}

interface RankedRow {
  label: string;
  n: number;
  comp: number | null;
  stat: number | null;
  cand: number | null;
  thin: boolean;
  filter: SegmentFilter;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PAGE_SIZE = 50;

/**
 * Valuation Lab (DRAFT) — how the comparables engine and the statistical
 * model did against all-in auction results, lot by lot, with the reasons
 * behind each estimate and a feedback loop on the comparables it used.
 *
 * Data: the PlateX assistant server (/valuation-lab/*), admin-only, see
 * ValuationLabService. Neither method is adjusted by the other: each is
 * shown exactly as it came out.
 */
@Component({
  selector: 'pla-admin-valuation-lab',
  templateUrl: './admin-valuation-lab.component.html',
  styleUrls: ['./admin-valuation-lab.component.scss'],
})
export class AdminValuationLabComponent implements OnInit {
  @ViewChild('detailPanel') detailPanel: ElementRef<HTMLElement>;
  @ViewChild('streamPanel') streamPanel: ElementRef<HTMLElement>;

  public isAdmin = false;
  public isPhone = false;

  // ---- controls
  public plateInput = '';
  public asOf = isoToday();
  public engine = '';
  public view: 'scorecard' | 'research' | 'upcoming' = 'scorecard';
  /** upcoming-auction readings not yet reviewed; null until known */
  public upcomingPending: number | null = null;
  public upcomingVisited = false;
  /** stream filter: lots whose all-in result sits outside the range */
  public outside: VlOutside | 'either' | '' = '';
  public outsideCounts: { above: number; below: number } | null = null;
  /** queued + researched (awaiting review); null until known. */
  public researchPending: number | null = null;
  /** Whole research queue by status (breakdown line under the view switch). */
  public researchCounts: Record<string, number> | null = null;
  /** Research view is created on first open, then kept mounted. */
  public researchVisited = false;
  public method: Method = 'both';
  public readonly methods = METHODS;
  /** Which engine's reasoning / comparables the detail panel shows. */
  public detailEngine: 'cand' | 'comp' = 'cand';
  public source: VlSource = 'all';

  // ---- summary
  public summary: VlSummary | null = null;
  public summaryLoading = false;
  public summaryError: string | null = null;
  public breakdown: Breakdown = 'grid';

  // ---- stream
  public lots: VlLot[] = [];
  public total = 0;
  public page = 1;
  public lotsLoading = false;
  public lotsError: string | null = null;
  public house = '';
  public status: VlStatus | '' = '';
  public sort: VlSort = 'closes_desc';
  public q = '';
  public segment: SegmentFilter | null = null;

  // ---- detail
  public detail: Detail | null = null;
  public detailLoading = false;
  public detailError: string | null = null;
  public selectedKey: string | null = null;
  public shouldBeHere = '';
  public savedComps: Record<string, 'saving' | 'saved' | 'error'> = {};
  public suggestState: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  public feedbackError: string | null = null;

  // ---- "Wrong comp" context row (one open at a time)
  public readonly wrongReasons = WRONG_REASONS;
  public readonly noteCategories = RESEARCH_CATEGORIES;
  public wrongOpen: string | null = null;
  public wrongReason: VlReasonCategory | null = null;
  public wrongNote = '';
  public wrongAlsoMeaning = true;
  public wrongSaving = false;
  public wrongError: string | null = null;
  /** per comp: what happened to the "also note what X means" request */
  public meaningSaved: Record<string, string> = {};

  // ---- "About this plate"
  public aboutWhy = '';
  public aboutCategory = '';
  public aboutSaving = false;
  public aboutSaved = false;
  public aboutError: string | null = null;

  // ---- "Comment on this valuation"
  public readonly commentTopics = COMMENT_TOPICS;
  public commentText = '';
  public commentTopicsOn: Record<string, boolean> = {};
  public commentSaving = false;
  public commentSaved = false;
  public commentError: string | null = null;

  public readonly breakdownTabs: { id: Breakdown; label: string }[] = [
    { id: 'grid', label: 'Style × length' },
    { id: 'number_block', label: 'Number block' },
    { id: 'price_band', label: 'Price band' },
    { id: 'house', label: 'House' },
    { id: 'confidence', label: 'Confidence' },
  ];
  public readonly statuses: { id: VlStatus; label: string }[] = [
    { id: 'sold', label: 'Sold (scored)' },
    { id: 'open', label: 'Open — estimate locked' },
    { id: 'awaiting_result', label: 'Awaiting result' },
    { id: 'result_missing', label: 'Result missing' },
    { id: 'unsold', label: 'Unsold (not scored)' },
  ];

  private summarySeq = 0;
  private lotsSeq = 0;
  private detailSeq = 0;

  constructor(
    private lab: ValuationLabService,
    private sessionService: SessionService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.onResize();
    // localDevAccess: localhost-only developer route (src/app/dev-access.ts); never on a deployed site.
    this.isAdmin = this.sessionService.isAdmin() || localDevAccess;
    if (!this.isAdmin) return;
    this.reload();
    this.loadResearchCount();
    this.loadUpcomingCount();
  }

  // ================================================================ views

  public setView(v: 'scorecard' | 'research' | 'upcoming'): void {
    this.view = v;
    if (v === 'research') this.researchVisited = true;
    if (v === 'upcoming') this.upcomingVisited = true;
  }

  public onUpcomingPending(n: number): void {
    this.upcomingPending = n;
  }

  private loadUpcomingCount(): void {
    this.lab
      .auctionReadings({ page: 1, page_size: 1 })
      .then((r) =>
        this.apply(() => {
          const c = r && r.counts;
          if (c) this.upcomingPending = Math.max(0, (c.with_reading || 0) - (c.reviewed || 0));
        })
      )
      .catch(() => {});
  }

  public onResearchPending(n: number): void {
    this.researchPending = n;
  }

  private loadResearchCount(): void {
    this.lab
      .research({ page: 1, page_size: 1 })
      .then((r) =>
        this.apply(() => {
          const c = (r && r.counts) || {};
          this.researchCounts = c as Record<string, number>;
          this.researchPending = (c.queued || 0) + (c.researched || 0);
        })
      )
      .catch(() => {});
  }

  /** Research queue → "Open lot in scorecard": switch view and open that lot's detail. */
  public openLotFromResearch(lotKey: string): void {
    this.setView('scorecard');
    // lot_key carries no source field; back-test keys are prefixed "bt:".
    this.openLotKey(lotKey, lotKey.indexOf('bt:') === 0 ? 'backtest' : 'live');
    this.scrollTo(this.detailPanel, true);
  }

  public openLotKey(lotKey: string, source: VlSource | string): void {
    const seq = ++this.detailSeq;
    this.selectedKey = lotKey;
    this.resetFeedbackState();
    this.detail = null;
    this.detailLoading = true;
    this.detailError = null;
    this.lab
      .lot(lotKey, source)
      .then((d) =>
        this.apply(() => {
          if (seq !== this.detailSeq) return;
          this.detail = this.fromLot({ lot_key: lotKey, source, ...d } as VlLotDetail, true);
          this.detailEngine = this.hasCandDetail(this.detail) ? 'cand' : 'comp';
        })
      )
      .catch((e) => this.apply(() => seq === this.detailSeq && (this.detailError = errText(e))))
      .finally(() => this.apply(() => seq === this.detailSeq && (this.detailLoading = false)));
  }

  @HostListener('window:resize')
  onResize(): void {
    if (typeof window === 'undefined') return;
    this.isPhone = window.innerWidth < 768;
  }

  // ===================================================================== loads

  /** Re-query summary + first page of lots (source change, retry). */
  public reload(): void {
    this.loadSummary();
    this.loadLots(false);
  }

  public loadSummary(): void {
    const seq = ++this.summarySeq;
    this.summaryLoading = true;
    this.summaryError = null;
    this.lab
      .summary({ source: this.source })
      .then((s) =>
        this.apply(() => {
          if (seq !== this.summarySeq) return;
          this.summary = s;
          if (!this.engine && s.engines && s.engines.length) this.engine = s.engines[0];
        })
      )
      .catch((e) =>
        this.apply(() => {
          if (seq !== this.summarySeq) return;
          this.summary = null;
          this.summaryError = errText(e);
        })
      )
      .finally(() => this.apply(() => seq === this.summarySeq && (this.summaryLoading = false)));
  }

  public loadLots(append: boolean): void {
    const seq = ++this.lotsSeq;
    const page = append ? this.page + 1 : 1;
    this.lotsLoading = true;
    this.lotsError = null;
    const s = this.segment;
    this.lab
      .lots({
        source: this.source,
        house: this.house || undefined,
        status: this.status || undefined,
        style: s ? s.style : undefined,
        length: s ? s.length : undefined,
        segment_type: s ? s.segment_type : undefined,
        segment_key: s ? s.segment_key : undefined,
        outside: this.outside || undefined,
        q: this.q.trim() ? normPlate(this.q) : undefined,
        sort: this.sort,
        page,
        page_size: PAGE_SIZE,
      })
      .then((r) =>
        this.apply(() => {
          if (seq !== this.lotsSeq) return;
          const rows = (r && r.lots) || [];
          this.lots = append ? this.lots.concat(rows) : rows;
          this.total = r && typeof r.total === 'number' ? r.total : this.lots.length;
          if (r && r.outside_counts) this.outsideCounts = r.outside_counts;
          this.page = (r && r.page) || page;
        })
      )
      .catch((e) =>
        this.apply(() => {
          if (seq !== this.lotsSeq) return;
          if (!append) this.lots = [];
          this.lotsError = errText(e);
        })
      )
      .finally(() => this.apply(() => seq === this.lotsSeq && (this.lotsLoading = false)));
  }

  public get canLoadMore(): boolean {
    return !this.lotsLoading && this.lots.length < this.total;
  }

  public setSource(src: VlSource): void {
    if (this.source === src) return;
    this.source = src;
    this.reload();
  }

  public setMethod(m: Method): void {
    this.method = m;
  }

  /** Methods shown in KPIs / grid / ranked lists: one, or all three side by side. */
  public get shownMethods(): MethodDef[] {
    return this.method === 'both' ? METHODS : METHODS.filter((m) => m.key === this.method);
  }

  /** In single-method mode the other methods stay visible but dimmed. */
  public isDim(m: VlMethodKey): boolean {
    return this.method !== 'both' && this.method !== m;
  }

  public setDetailEngine(e: 'cand' | 'comp'): void {
    this.detailEngine = e;
  }

  public onStreamFilterChange(): void {
    this.loadLots(false);
  }

  public setSegment(f: SegmentFilter): void {
    const same =
      this.segment &&
      this.segment.style === f.style &&
      this.segment.length === f.length &&
      this.segment.segment_type === f.segment_type &&
      this.segment.segment_key === f.segment_key;
    this.segment = same ? null : f;
    this.loadLots(false);
    if (this.segment && this.isPhone) this.scrollTo(this.streamPanel);
  }

  public clearSegment(): void {
    this.segment = null;
    this.loadLots(false);
  }

  // ==================================================================== detail

  public openLot(l: VlLot): void {
    const seq = ++this.detailSeq;
    this.selectedKey = l.lot_key;
    this.resetFeedbackState();
    // Show the row's numbers immediately; explanation + comps arrive with /lot.
    this.detail = this.fromLot(l as VlLotDetail, false);
    this.detailLoading = true;
    this.detailError = null;
    if (this.isPhone) this.scrollTo(this.detailPanel);
    this.lab
      .lot(l.lot_key, l.source)
      .then((d) =>
        this.apply(() => {
          if (seq !== this.detailSeq) return;
          this.detail = this.fromLot({ ...l, ...d }, true);
          this.detailEngine = this.hasCandDetail(this.detail) ? 'cand' : 'comp';
        })
      )
      .catch((e) => this.apply(() => seq === this.detailSeq && (this.detailError = errText(e))))
      .finally(() => this.apply(() => seq === this.detailSeq && (this.detailLoading = false)));
  }

  public valuePlate(): void {
    const plate = normPlate(this.plateInput);
    if (!plate) return;
    const seq = ++this.detailSeq;
    this.selectedKey = null;
    this.resetFeedbackState();
    this.detail = null;
    this.detailLoading = true;
    this.detailError = null;
    this.scrollTo(this.detailPanel);
    this.lab
      .value(plate, this.asOf || null)
      .then((v) =>
        this.apply(() => {
          if (seq !== this.detailSeq) return;
          this.detail = this.fromValue(plate, v);
          this.detailEngine = this.hasCandDetail(this.detail) ? 'cand' : 'comp';
        })
      )
      .catch((e) => this.apply(() => seq === this.detailSeq && (this.detailError = errText(e))))
      .finally(() => this.apply(() => seq === this.detailSeq && (this.detailLoading = false)));
  }

  /** "Wrong comp" opens an inline "why is it wrong?" row under that comparable. */
  public wrongComp(c: VlComp): void {
    if (!this.detail || this.savedComps[c.registration] === 'saving' || this.savedComps[c.registration] === 'saved') return;
    if (this.wrongOpen === c.registration) return;
    this.wrongOpen = c.registration;
    this.wrongReason = null;
    this.wrongNote = '';
    this.wrongAlsoMeaning = true;
    this.wrongError = null;
    setTimeout(() => {
      const el = typeof document !== 'undefined' ? document.getElementById('wrong-reason-0') : null;
      if (el) el.focus();
    }, 0);
  }

  public cancelWrong(): void {
    this.wrongOpen = null;
    this.wrongError = null;
  }

  public setWrongReason(r: VlReasonCategory): void {
    this.wrongReason = r;
    this.wrongError = null;
  }

  /** Ctrl/Cmd+Enter in the context box saves; plain Enter is just a new line. */
  public onWrongKeydown(e: KeyboardEvent, c: VlComp): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.saveWrong(c);
    } else if (e.key === 'Escape') {
      this.cancelWrong();
    }
  }

  public saveWrong(c: VlComp): void {
    const d = this.detail;
    if (!d || this.wrongSaving) return;
    if (!this.wrongReason) {
      this.wrongError = 'Pick a reason first.';
      return;
    }
    const reason = this.wrongReason;
    const note = this.wrongNote.trim().slice(0, 1000);
    const alsoMeaning = reason === 'meaning_premium' && this.wrongAlsoMeaning && !!note;
    this.wrongSaving = true;
    this.wrongError = null;
    this.savedComps[c.registration] = 'saving';
    this.lab
      .feedback({
        lot_key: d.lotKey,
        plate: d.plate,
        comp_registration: c.registration,
        kind: 'wrong_comp',
        note,
        reason_category: reason,
      })
      .then((r) =>
        this.apply(() => {
          if (this.detail !== d) return;
          this.savedComps[c.registration] = 'saved';
          this.pushFeedback(d, r && r.id, 'wrong_comp', c.registration, note, reason);
          this.wrongOpen = null;
          if (alsoMeaning) {
            this.meaningSaved[c.registration] = 'saving';
            this.lab
              .researchNotes([{ plate: c.registration, why: note, category: 'word' }])
              .then((n) =>
                this.apply(() => {
                  const skipped = n && n.skipped && n.skipped.indexOf(c.registration) >= 0;
                  this.meaningSaved[c.registration] = skipped ? 'meaning note skipped' : 'meaning noted';
                })
              )
              .catch((e) => this.apply(() => (this.meaningSaved[c.registration] = 'meaning note failed: ' + errText(e))));
          }
        })
      )
      .catch((e) =>
        this.apply(() => {
          if (this.detail !== d) return;
          this.savedComps[c.registration] = 'error';
          this.wrongError = errText(e);
        })
      )
      .finally(() => this.apply(() => (this.wrongSaving = false)));
  }

  public reasonLabel(r: string | null | undefined): string {
    if (!r) return '';
    const hit = WRONG_REASONS.find((x) => x.id === r);
    return hit ? hit.label : r.replace(/_/g, ' ');
  }

  public noteCategoryLabel(c: string | null | undefined): string {
    if (!c) return '';
    const hit = RESEARCH_CATEGORIES.find((x) => x.id === c);
    return hit ? hit.label : c.replace(/_/g, ' ');
  }

  // ------------------------------------------------------- about this plate

  public saveAbout(): void {
    const d = this.detail;
    const why = this.aboutWhy.trim();
    if (!d || !why || this.aboutSaving) return;
    this.aboutSaving = true;
    this.aboutSaved = false;
    this.aboutError = null;
    const item = this.aboutCategory ? { plate: d.plate, why, category: this.aboutCategory } : { plate: d.plate, why };
    this.lab
      .researchNotes([item])
      .then((r) =>
        this.apply(() => {
          if (this.detail !== d) return;
          if (r && r.skipped && r.skipped.indexOf(d.plate) >= 0) {
            this.aboutError = 'Not saved — the server skipped this plate.';
            return;
          }
          d.plateNotes = [{ why, category: this.aboutCategory || null, at: new Date().toISOString(), by: 'you' }].concat(d.plateNotes);
          this.aboutWhy = '';
          this.aboutCategory = '';
          this.aboutSaved = true;
        })
      )
      .catch((e) => this.apply(() => this.detail === d && (this.aboutError = errText(e))))
      .finally(() => this.apply(() => (this.aboutSaving = false)));
  }

  public onAboutKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.saveAbout();
    }
  }

  public suggest(): void {
    const d = this.detail;
    const reg = normPlate(this.shouldBeHere);
    if (!d || !reg || this.suggestState === 'saving') return;
    this.suggestState = 'saving';
    this.feedbackError = null;
    this.lab
      .feedback({ lot_key: d.lotKey, plate: d.plate, comp_registration: reg, kind: 'should_be_here', note: '' })
      .then((r) =>
        this.apply(() => {
          if (this.detail !== d) return;
          this.suggestState = 'saved';
          this.shouldBeHere = '';
          this.pushFeedback(d, r && r.id, 'should_be_here', reg);
        })
      )
      .catch((e) =>
        this.apply(() => {
          if (this.detail !== d) return;
          this.suggestState = 'error';
          this.feedbackError = errText(e);
        })
      );
  }

  public onSuggestInput(): void {
    if (this.suggestState === 'saved' || this.suggestState === 'error') this.suggestState = 'idle';
  }

  public alreadyFlagged(reg: string): boolean {
    return !!this.detail && this.detail.feedback.some((f) => f.kind === 'wrong_comp' && f.comp_registration === reg);
  }

  private pushFeedback(d: Detail, id: string, kind: VlFeedback['kind'], reg: string, note = '', reason: string | null = null): void {
    d.feedback = d.feedback.concat([
      { id: id || '', kind, comp_registration: reg, note, reason_category: reason, at: new Date().toISOString(), by: 'you' },
    ]);
  }

  private resetFeedbackState(): void {
    this.savedComps = {};
    this.suggestState = 'idle';
    this.shouldBeHere = '';
    this.feedbackError = null;
    this.wrongOpen = null;
    this.wrongError = null;
    this.meaningSaved = {};
    this.aboutWhy = '';
    this.aboutCategory = '';
    this.aboutSaved = false;
    this.aboutError = null;
    this.commentText = '';
    this.commentTopicsOn = {};
    this.commentSaved = false;
    this.commentError = null;
  }

  // ------------------------------------------------- comment on this valuation

  public toggleTopic(t: string): void {
    this.commentTopicsOn[t] = !this.commentTopicsOn[t];
  }

  /** The engine whose reasoning the panel is showing (candidate when its tab is on). */
  public commentEngine(d: Detail): string {
    if (this.detailEngine === 'cand' && this.hasCandDetail(d)) {
      return (d.cand && d.cand.engine) || (this.summary && this.summary.candidate_engine) || 'candidate';
    }
    return (d.comp && d.comp.engine) || (this.engines[0] || 'dateless-comparable-v1');
  }

  public saveComment(): void {
    const d = this.detail;
    const comment = this.commentText.trim();
    if (!d || !comment || this.commentSaving) return;
    const topics = COMMENT_TOPICS.filter((t) => this.commentTopicsOn[t.id]).map((t) => t.id);
    const engine = this.commentEngine(d);
    this.commentSaving = true;
    this.commentSaved = false;
    this.commentError = null;
    this.lab
      .comment({ plate: d.plate, lot_key: d.lotKey, comment, topics: topics.length ? topics : undefined, engine })
      .then((r) =>
        this.apply(() => {
          if (this.detail !== d) return;
          d.comments = d.comments.concat([
            { id: (r && r.id) || '', plate: d.plate, lot_key: d.lotKey, comment, topics, engine, status: 'open', reply: null, at: new Date().toISOString(), by: 'you' },
          ]);
          this.commentText = '';
          this.commentTopicsOn = {};
          this.commentSaved = true;
        })
      )
      .catch((e) => this.apply(() => this.detail === d && (this.commentError = errText(e))))
      .finally(() => this.apply(() => (this.commentSaving = false)));
  }

  public onCommentKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.saveComment();
    }
  }

  public topicLabel(t: string): string {
    const hit = COMMENT_TOPICS.find((x) => x.id === t);
    return hit ? hit.label : t.replace(/_/g, ' ');
  }

  private fromLot(l: VlLotDetail, full: boolean): Detail {
    const ex = this.explain(l);
    return {
      mode: 'lot',
      lotKey: l.lot_key,
      plate: l.registration,
      houseLine: [l.house_label || l.house, shortDate(l.closes_at)].filter(Boolean).join(' · '),
      segment: l.segment_label,
      source: l.source,
      status: l.status,
      comp: okComp(l.comp),
      compError: methodErr(l.comp),
      stat: okStat(l.stat),
      statError: methodErr(l.stat),
      cand: okCand(l.cand),
      candError: methodErr(l.cand),
      allIn: l.result ? l.result.all_in_pence : null,
      allInBasis: l.result ? l.result.basis : null,
      compErrPct: l.comp_error_pct,
      statErrPct: l.stat_error_pct,
      candErrPct: l.cand_error_pct ?? null,
      compInRange: l.comp_in_range,
      statInRange: l.stat_in_range,
      candInRange: l.cand_in_range ?? null,
      explanation: full ? ex.explanation : [],
      comps: full ? ex.comps : [],
      candExplanation: full ? ex.candExplanation : [],
      candComps: full ? ex.candComps : [],
      candCompGroups: full ? ex.candCompGroups : [],
      compsTotal: full ? ex.compsTotal : null,
      excluded: full ? ex.excluded : [],
      feedback: (full && l.feedback) || [],
      plateNotes: full ? sortNotes(l.plate_notes) : [],
      comments: (full && l.comments) || [],
      outside: l.outside || null,
    };
  }

  private fromValue(plate: string, v: VlValueResponse): Detail {
    const ex = this.explain(v);
    return {
      mode: 'value',
      lotKey: null,
      plate: v.plate || plate,
      houseLine: 'Valued as of ' + longDate(v.as_of || this.asOf),
      segment: null,
      source: null,
      status: null,
      comp: okComp(v.comp),
      compError: methodErr(v.comp),
      stat: okStat(v.stat),
      statError: methodErr(v.stat),
      cand: okCand(v.cand),
      candError: methodErr(v.cand),
      allIn: null,
      allInBasis: null,
      compErrPct: null,
      statErrPct: null,
      candErrPct: null,
      compInRange: null,
      statInRange: null,
      candInRange: null,
      explanation: ex.explanation,
      comps: ex.comps,
      candExplanation: ex.candExplanation,
      candComps: ex.candComps,
      candCompGroups: ex.candCompGroups,
      compsTotal: ex.compsTotal,
      excluded: ex.excluded,
      feedback: v.feedback || [],
      plateNotes: sortNotes(v.plate_notes),
      comments: [],
      outside: null,
    };
  }

  private explain(x: {
    explanation?: string[] | null;
    comps?: VlComp[] | null;
    comps_total?: number | null;
    excluded_summary?: Record<string, number> | null;
    excluded_labels?: Record<string, string> | null;
    cand_explanation?: string[] | null;
    cand_comps?: VlComp[] | null;
  }) {
    const candComps = x.cand_comps || [];
    const summary = x.excluded_summary || {};
    const labels = x.excluded_labels || {};
    const excluded = Object.keys(summary)
      .filter((k) => typeof summary[k] === 'number' && summary[k] > 0)
      .map((k) => ({ key: k, n: summary[k], label: labels[k] || k.replace(/_/g, ' ') }))
      .sort((a, b) => b.n - a.n);
    return {
      explanation: (x.explanation || []).filter((s) => !!s),
      comps: x.comps || [],
      compsTotal: typeof x.comps_total === 'number' ? x.comps_total : null,
      excluded,
      candExplanation: (x.cand_explanation || []).filter((s) => !!s),
      candComps,
      candCompGroups: [
        { title: 'Same number block', rows: candComps.filter((c) => c.role === 'base') },
        { title: 'Same letters, any format', rows: candComps.filter((c) => c.role === 'letters') },
        { title: 'Other', rows: candComps.filter((c) => c.role !== 'base' && c.role !== 'letters') },
      ].filter((g) => g.rows.length),
    };
  }

  // ============================================================ summary views

  public get freshness(): VlFreshness[] {
    return (this.summary && this.summary.freshness) || [];
  }

  public get staleFreshness(): VlFreshness[] {
    return this.freshness.filter((f) => f.status !== 'ok');
  }

  public get statK(): VlMethodKpis | null {
    return (this.summary && this.summary.kpis && this.summary.kpis.stat) || null;
  }

  public get closer() {
    return (this.summary && this.summary.kpis && this.summary.kpis.closer) || null;
  }

  public kpi(m: VlMethodKey): VlMethodKpis | null {
    const k = this.summary && this.summary.kpis;
    return (k && (k as any)[m]) || null;
  }

  /** Head-to-head "closer to the result" pairs, candidate first. */
  public get closerPairs(): { a: string; an: number; b: string; bn: number; tie: number; compared: number }[] {
    const k = this.summary && this.summary.kpis;
    if (!k) return [];
    const out = [];
    const cc = k.closer_cand_comp;
    if (cc) out.push({ a: 'cand', an: cc.cand, b: 'v1', bn: cc.comp, tie: cc.tie, compared: cc.compared });
    const cs = k.closer_cand_stat;
    if (cs) out.push({ a: 'cand', an: cs.cand, b: 'stat', bn: cs.stat, tie: cs.tie, compared: cs.compared });
    const c = k.closer;
    if (c) out.push({ a: 'v1', an: c.comp, b: 'stat', bn: c.stat, tie: c.tie, compared: c.compared });
    return out;
  }

  public get candScope(): string {
    return (this.summary && this.summary.candidate_scope) || 'dateless plates, back-test lots';
  }

  /** Median error of a grid cell / breakdown / ranked row for one method. */
  public segErr(x: any, m: VlMethodKey): number | null {
    if (!x) return null;
    const v = x[m + '_median_error_pct'] !== undefined ? x[m + '_median_error_pct'] : x[m];
    return v === undefined ? null : v;
  }

  /** A lot's estimate for one method, normalised to low/mid/high (stat → 80% range). */
  public lotEst(l: VlLot | Detail, m: VlMethodKey): Est | null {
    const raw: any = (l as any)[m];
    if (!raw || isErr(raw)) return null;
    if (m === 'stat') return { low: raw.low80 ?? null, mid: raw.mid ?? null, high: raw.high80 ?? null };
    return { low: raw.low ?? null, mid: raw.mid ?? null, high: raw.high ?? null };
  }

  /** The method's {"error": …} text, if it returned one instead of numbers. */
  public lotMethodErr(l: VlLot, m: VlMethodKey): string | null {
    return methodErr((l as any)[m]);
  }

  public lotErrPct(l: VlLot, m: VlMethodKey): number | null {
    const v = (l as any)[m + '_error_pct'];
    return v === undefined ? null : v;
  }

  public lotConfidence(l: VlLot, m: VlMethodKey): string | null {
    const raw: any = (l as any)[m];
    return raw && !isErr(raw) ? raw.confidence || null : null;
  }

  /** Stat lots sold before its training cut-off are not scored for stat. */
  public inTraining(l: VlLot, m: VlMethodKey): boolean {
    if (m !== 'stat') return false;
    const s: any = l.stat;
    return !!s && !isErr(s) && !!s.in_training;
  }

  public hasCandDetail(d: Detail | null): boolean {
    return !!d && (d.candExplanation.length > 0 || d.candComps.length > 0);
  }

  /** "out-performs" → "letters out-perform"; anything else is shown as given. */
  public letterVerdict(v: string | null | undefined): string {
    if (!v) return '';
    const t = v.trim().replace(/_/g, ' ');
    const words = t.split(' ');
    if (/s$/.test(words[0]) && words[0].length > 3) words[0] = words[0].slice(0, -1);
    return 'letters ' + words.join(' ');
  }

  public verdictClass(v: string | null | undefined): string {
    const t = (v || '').toLowerCase();
    if (t.indexOf('out') === 0 || t.indexOf('over') === 0 || t.indexOf('beat') === 0) return 'v-up';
    if (t.indexOf('under') === 0 || t.indexOf('lag') === 0) return 'v-down';
    return 'v-flat';
  }

  public get engines(): string[] {
    return (this.summary && this.summary.engines) || [];
  }

  public get minN(): number {
    const c = this.summary && this.summary.grid && this.summary.grid.cells && this.summary.grid.cells[0];
    return c && typeof c.min_n === 'number' ? c.min_n : 10;
  }

  public get houseOptions(): { key: string; label: string }[] {
    const b = this.summary && this.summary.breakdowns && this.summary.breakdowns.house;
    if (b && b.length) return b.map((r) => ({ key: r.key, label: r.label || r.key }));
    return this.freshness.map((f) => ({ key: f.house, label: f.label || f.house }));
  }

  /** Grid rows in render order; each cell is looked up by style|length. */
  public get gridRows(): { style: string; label: string; cells: (VlGridCell | null)[] }[] {
    const g = this.summary && this.summary.grid;
    if (!g || !g.styles || !g.lengths) return [];
    const map: Record<string, VlGridCell> = {};
    (g.cells || []).forEach((c) => (map[c.style + '|' + c.length] = c));
    return g.styles.map((s) => ({
      style: s,
      label: titleCase(s),
      cells: g.lengths.map((len) => map[s + '|' + len] || null),
    }));
  }

  public get gridLengths(): number[] {
    return (this.summary && this.summary.grid && this.summary.grid.lengths) || [];
  }

  public get gridColumns(): string {
    return '112px repeat(' + this.gridLengths.length + ', minmax(104px, 1fr))';
  }

  public get gridMinWidth(): number {
    return 112 + this.gridLengths.length * 110;
  }

  /** Ranked rows: phone "worst segments" for the grid, or a breakdown list. */
  public get rankedRows(): RankedRow[] {
    if (!this.summary) return [];
    let rows: RankedRow[];
    if (this.breakdown === 'grid') {
      const cells = (this.summary.grid && this.summary.grid.cells) || [];
      rows = cells.map((c) => ({
        label: titleCase(c.style) + ' · ' + c.length,
        n: c.n,
        comp: c.comp_median_error_pct,
        stat: c.stat_median_error_pct,
        cand: c.cand_median_error_pct ?? null,
        thin: c.n < (c.min_n || this.minN),
        filter: { label: titleCase(c.style) + ' · ' + c.length + ' chars', style: c.style, length: c.length },
      }));
    } else {
      const type = this.breakdown;
      const list: VlBreakdownRow[] = (this.summary.breakdowns && this.summary.breakdowns[type]) || [];
      rows = list.map((r) => ({
        label: r.label || r.key,
        n: r.n,
        comp: r.comp_median_error_pct,
        stat: r.stat_median_error_pct,
        cand: r.cand_median_error_pct ?? null,
        thin: r.n < this.minN,
        filter: { label: r.label || r.key, segment_type: type, segment_key: r.key },
      }));
    }
    // Segments with no scored lots (e.g. 2-char prefix plates can't exist) are noise in a ranked list.
    return rows.filter((r) => r.n > 0).sort((a, b) => {
      if (a.thin !== b.thin) return a.thin ? 1 : -1;
      return this.rankErr(b) - this.rankErr(a);
    });
  }

  private rankErr(r: RankedRow): number {
    const a = (v: number | null) => (v == null ? -1 : Math.abs(v));
    return Math.max(...this.shownMethods.map((m) => a(r[m.key])));
  }

  public isSegmentActive(f: SegmentFilter): boolean {
    const s = this.segment;
    return (
      !!s && s.style === f.style && s.length === f.length && s.segment_type === f.segment_type && s.segment_key === f.segment_key
    );
  }

  public cellFilter(style: string, len: number): SegmentFilter {
    return { label: titleCase(style) + ' · ' + len + ' chars', style, length: len };
  }

  public cellThin(c: VlGridCell | null): boolean {
    return !c || c.n < (c.min_n || this.minN);
  }

  public cellAria(style: string, len: number, c: VlGridCell | null): string {
    if (!c) return titleCase(style) + ', ' + len + ' characters: no lots';
    return (
      titleCase(style) + ', ' + len + ' characters: ' +
      this.shownMethods.map((m) => m.label + ' ' + this.errWord(this.segErr(c, m.key))).join(', ') +
      ', ' + c.n + ' lots'
    );
  }

  // ================================================================ formatting

  /** Diverging fill: orange = under-estimate, blue = over; stronger = bigger miss. */
  public heat(err: number | null, thin = false): string {
    if (thin || err === null || err === undefined || isNaN(err)) return 'transparent';
    const m = Math.min(Math.abs(err), 60) / 60;
    const a = (0.1 + 0.62 * m).toFixed(2);
    return err < 0 ? 'rgba(232, 116, 48, ' + a + ')' : 'rgba(54, 104, 214, ' + a + ')';
  }

  public pct(n: number | null | undefined, digits = 0): string {
    if (n === null || n === undefined || isNaN(n)) return '—';
    return (+n).toFixed(digits) + '%';
  }

  /** Signed error → plain words: "Under 65%" / "Over 12%" / "On the money". */
  public errWord(e: number | null | undefined): string {
    if (e === null || e === undefined || isNaN(e)) return '—';
    const r = Math.round(Math.abs(e));
    if (r === 0) return 'On the money';
    return (e < 0 ? 'Under ' : 'Over ') + r + '%';
  }

  /** Compact grid form: "−12%" / "+4%". */
  public errShort(e: number | null | undefined): string {
    if (e === null || e === undefined || isNaN(e)) return '—';
    const r = Math.round(e);
    return (r > 0 ? '+' : r < 0 ? '−' : '') + Math.abs(r) + '%';
  }

  public errClass(e: number | null | undefined): string {
    if (e === null || e === undefined || isNaN(e)) return 'err-na';
    return e < 0 ? 'err-under' : e > 0 ? 'err-over' : 'err-zero';
  }

  public gbp(p: number | null | undefined): string {
    return gbp(p);
  }

  /** Range in £; compact (£3.7k–£6.9k) on narrow screens or when asked. */
  public range(lo: number | null | undefined, hi: number | null | undefined, mid?: number | null, compact?: boolean): string {
    const c = compact === undefined ? this.isPhone : compact;
    const f = c ? gbpShort : gbp;
    if (lo == null || hi == null) return mid != null ? f(mid) : '—';
    return f(lo) + '–' + f(hi);
  }

  public shortDate(s: string | null | undefined): string {
    return shortDate(s);
  }

  public longDate(s: string | null | undefined): string {
    return longDate(s);
  }

  public statusLabel(s: VlStatus | null): string {
    switch (s) {
      case 'open': return 'Open · estimate locked';
      case 'awaiting_result': return 'Awaiting result';
      case 'result_missing': return 'Result missing';
      case 'sold': return 'Sold · scored';
      case 'unsold': return 'Unsold';
      default: return '—';
    }
  }

  public resultText(l: { status: VlStatus | null; result?: { all_in_pence: number | null } | null; allIn?: number | null }): string {
    const p = l.result ? l.result.all_in_pence : l.allIn;
    if (l.status === 'unsold') return 'Not sold';
    if (l.status === 'result_missing') return 'Missing';
    if (p == null) return '—';
    return gbp(p);
  }

  public scored(l: { status: VlStatus | null }): boolean {
    return l.status === 'sold';
  }

  public setsLabel(s: VlComp['sets']): string {
    if (s === 'median') return 'sets median';
    if (s === 'q1') return 'sets low quartile';
    if (s === 'q3') return 'sets high quartile';
    return '';
  }

  public feedbackLine(f: VlFeedback): string {
    const what = f.kind === 'wrong_comp' ? 'Wrong comp' : 'Should be here';
    return what + (f.comp_registration ? ': ' + f.comp_registration : '');
  }

  public dateTime(s: string | null | undefined): string {
    const d = s ? new Date(s) : null;
    if (!d || isNaN(d.getTime())) return '';
    const p = (n: number) => (n < 10 ? '0' + n : '' + n);
    return longDate(s) + ', ' + p(d.getHours()) + ':' + p(d.getMinutes());
  }

  public feedbackMeta(f: VlFeedback): string {
    return [f.by, f.at ? longDate(f.at) : null].filter(Boolean).join(' · ');
  }

  public trackLot(_: number, l: VlLot): string {
    return l.lot_key;
  }

  public trackComp(_: number, c: VlComp): string {
    return c.registration;
  }

  // ================================================================== helpers

  private apply(fn: () => any): void {
    this.zone.run(() => {
      fn();
      this.cdr.markForCheck();
    });
  }

  private scrollTo(ref: ElementRef<HTMLElement> | undefined, always = false): void {
    if (typeof window === 'undefined') return;
    setTimeout(() => {
      const el = ref && ref.nativeElement;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (always || r.top < 0 || r.top > window.innerHeight * 0.6) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }
}

// ------------------------------------------------------------------ pure utils

function sortNotes(n: VlPlateNote[] | null | undefined): VlPlateNote[] {
  return (n || []).filter((x) => x && x.why).slice().sort((a, b) => (b.at || '').localeCompare(a.at || ''));
}

function isErr(x: any): x is VlMethodError {
  return !!x && typeof x === 'object' && typeof x.error === 'string';
}

function okComp(x: VlCompEstimate | VlMethodError | null | undefined): VlCompEstimate | null {
  return x && !isErr(x) ? x : null;
}

function okCand(x: VlCandEstimate | VlMethodError | null | undefined): VlCandEstimate | null {
  return x && !isErr(x) ? x : null;
}

function okStat(x: VlStatEstimate | VlMethodError | null | undefined): VlStatEstimate | null {
  return x && !isErr(x) ? x : null;
}

function methodErr(x: any): string | null {
  return isErr(x) ? x.error : null;
}

function errText(e: any): string {
  if (e instanceof VlError) return e.message;
  return 'Valuation Lab data unavailable';
}

function normPlate(s: string): string {
  return (s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

export function gbp(p: number | null | undefined): string {
  if (p === null || p === undefined || isNaN(p)) return '—';
  return '£' + Math.round(p / 100).toLocaleString('en-GB');
}

export function gbpShort(p: number | null | undefined): string {
  if (p === null || p === undefined || isNaN(p)) return '—';
  const pounds = p / 100;
  if (pounds >= 1e6) return '£' + trim1(pounds / 1e6) + 'm';
  if (pounds >= 1000) return '£' + trim1(pounds / 1000) + 'k';
  return '£' + Math.round(pounds);
}

function trim1(n: number): string {
  return n >= 100 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
}

function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + 'T12:00:00') : new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function shortDate(s: string | null | undefined): string {
  const d = parseDate(s);
  return d ? d.getDate() + ' ' + MONTHS[d.getMonth()] : '';
}

function longDate(s: string | null | undefined): string {
  const d = parseDate(s);
  return d ? d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear() : '—';
}

function isoToday(): string {
  const d = new Date();
  const p = (n: number) => (n < 10 ? '0' + n : '' + n);
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}
